import React, { useState, useEffect, useContext } from 'react';
import { CALL_INTERACTION_STATUS, ICallInteraction, IDebitmanagment, REPOSSESSION_PRIORITY } from '../../../types/repossession.type';
import { useToastContext } from '../../../common/components/ui/toast/toast.context';
import ButtonWithLoader from '../../../common/components/ui/loading/ButtonLoading';
import { AuthContext } from '../../../common/components/auth/authContext/AuthContext';
import { getVehicleById } from '../../../common/services/vehicles.api';
import { createCallInteractions, sendRepossessionDebitManagement } from '../../../common/services/debit-management.api';
import { getRenterPayments } from '../../../common/services/payments.api';
import { PaymentInfo, PAYMENT_STATUS } from '../../../types/payment.type';
import { DebitNegotiationDashboard } from '../../renters/components/negotiation-debits/dashboard/ModalDebits';

interface NegotiationModalProps {
  initialIsOpen?: boolean;
  onClose: () => void;
  data: IDebitmanagment;
  onUpdateInteraction?: (updatedItem: IDebitmanagment) => void;
}

// ── helpers ──────────────────────────────────────────────────────────────────

const formatDate = (value: unknown) => {
  if (!value) return '';
  try {
    return new Intl.DateTimeFormat('pt-BR').format(new Date(String(value)));
  } catch {
    return String(value);
  }
};

const formatDateTime = (value: unknown) => {
  if (!value) return '';
  try {
    return new Intl.DateTimeFormat('pt-BR', { dateStyle: 'short', timeStyle: 'short' }).format(
      value instanceof Date ? value : new Date(String(value))
    );
  } catch {
    return String(value);
  }
};

const formatCurrency = (value: unknown) => {
  if (value === null || value === undefined) return 'R$ 0,00';
  return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(Number(value));
};

const NON_NEGOTIABLE_STATUSES = new Set<PAYMENT_STATUS>([
  PAYMENT_STATUS.RECEIVED,
  PAYMENT_STATUS.CONFIRMED,
  PAYMENT_STATUS.RECEIVED_IN_CASH,
  PAYMENT_STATUS.REFUNDED,
  PAYMENT_STATUS.RENEGOTIATED,
]);

const getLatestPaymentHistory = (payment: PaymentInfo) =>
  payment.history?.length ? payment.history[payment.history.length - 1]?.data || null : null;

const isOverduePayment = (payment: PaymentInfo) => {
  if (!payment) return false;
  const latestHistory = getLatestPaymentHistory(payment);
  const effectiveStatus = latestHistory?.status || payment.status;
  if (Boolean(latestHistory?.deleted)) return false;
  if (effectiveStatus === PAYMENT_STATUS.OVERDUE) return true;
  if (effectiveStatus && NON_NEGOTIABLE_STATUSES.has(effectiveStatus)) return false;
  const dueDateValue = payment.dueDate || latestHistory?.dueDate;
  if (!dueDateValue || Boolean(payment.paymentDate || latestHistory?.paymentDate)) return false;
  const dueDate = new Date(dueDateValue);
  return !Number.isNaN(dueDate.getTime()) && dueDate < new Date();
};

const mapPaymentsToNegotiationRows = (payments: PaymentInfo[]) =>
  payments
    .filter(isOverduePayment)
    .sort((a, b) => new Date(a.dueDate || 0).getTime() - new Date(b.dueDate || 0).getTime())
    .map((p) => ({
      description: p.description || '',
      dueDate: p.dueDate || p.paymentDate || '',
      individualAmountPayment: Number(p.value) || 0,
    }));

const INTERACTION_STATUS_LABELS: Record<CALL_INTERACTION_STATUS, string> = {
  [CALL_INTERACTION_STATUS.CONTACTED]: 'Contactado',
  [CALL_INTERACTION_STATUS.SCHEDULE]: 'Agendar Contato',
  [CALL_INTERACTION_STATUS.NEGOTIATED]: 'Negociado',
};

// ── sub-components ────────────────────────────────────────────────────────────

const RED = '#e44131';

const InfoCard = ({ label, children }: { label: string; children: React.ReactNode }) => (
  <div className="border border-gray-300 rounded-lg p-4 bg-white">
    <p className="text-xs font-semibold uppercase tracking-wider mb-1 text-gray-500">{label}</p>
    <div className="font-semibold text-sm text-gray-800">{children}</div>
  </div>
);

const SectionLabel = ({ children }: { children: React.ReactNode }) => (
  <p className="text-xs font-semibold uppercase tracking-wider mb-2 text-gray-500">{children}</p>
);

const TableLoadingSkeleton = () => (
  <div className="animate-pulse p-4 bg-white">
    <div className="h-8 bg-gray-200 rounded mb-1" />
    {[1, 2, 3].map((i) => <div key={i} className="h-7 bg-gray-100 rounded mb-1" />)}
  </div>
);

// ── RegisterInteractionModal ──────────────────────────────────────────────────

interface RegisterInteractionModalProps {
  onClose: () => void;
  onSubmit: (notes: string, status: CALL_INTERACTION_STATUS, scheduledDate?: string) => Promise<void>;
  isLoading: boolean;
}

const RegisterInteractionModal: React.FC<RegisterInteractionModalProps> = ({ onClose, onSubmit, isLoading }) => {
  const [notes, setNotes] = useState('');
  const [status, setStatus] = useState<CALL_INTERACTION_STATUS>(CALL_INTERACTION_STATUS.CONTACTED);
  const [scheduledDate, setScheduledDate] = useState('');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit(notes, status, status === CALL_INTERACTION_STATUS.SCHEDULE ? scheduledDate : undefined);
  };

  return (
    <div
      className="fixed inset-0 flex items-center justify-center z-[60]"
      style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}
      onClick={onClose}
    >
      <div
        className="bg-white rounded-xl shadow-2xl w-full max-w-md flex flex-col overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-300">
          <h3 className="font-semibold text-base text-gray-900">Registrar Interação</h3>
          <button
            onClick={onClose}
            aria-label="Fechar"
            className="w-8 h-8 rounded-full flex items-center justify-center text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
            </svg>
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-5 space-y-4">
          {/* Status */}
          <div>
            <label className="block text-xs font-semibold uppercase tracking-wider text-gray-500 mb-2">
              Status
            </label>
            <div className="flex gap-2">
              {Object.values(CALL_INTERACTION_STATUS).map((s) => (
                <button
                  key={s}
                  type="button"
                  onClick={() => setStatus(s)}
                  className="flex-1 py-2 px-3 rounded-lg text-sm font-medium border transition-colors"
                  style={status === s
                    ? { backgroundColor: RED, color: '#fff', borderColor: RED }
                    : { backgroundColor: '#fff', color: '#374151', borderColor: '#d1d5db' }
                  }
                >
                  {INTERACTION_STATUS_LABELS[s]}
                </button>
              ))}
            </div>
          </div>

          {/* Scheduled date — only when SCHEDULE */}
          {status === CALL_INTERACTION_STATUS.SCHEDULE && (
            <div>
              <label className="block text-xs font-semibold uppercase tracking-wider text-gray-500 mb-2">
                Data e Hora do próximo contato
              </label>
              <input
                type="datetime-local"
                value={scheduledDate}
                onChange={(e) => setScheduledDate(e.target.value)}
                required
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm text-gray-800 outline-none focus:ring-1"
                style={{ '--tw-ring-color': RED } as React.CSSProperties}
              />
            </div>
          )}

          {/* Observations */}
          <div>
            <label className="block text-xs font-semibold uppercase tracking-wider text-gray-500 mb-2">
              Observações <span style={{ color: RED }}>*</span>
            </label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={4}
              required
              placeholder="Descreva o contato realizado..."
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm text-gray-800 placeholder-gray-400 outline-none resize-none focus:ring-1"
              style={{ '--tw-ring-color': RED } as React.CSSProperties}
            />
          </div>

          {/* Footer */}
          <div className="flex justify-end gap-3 pt-1">
            <button
              type="button"
              onClick={onClose}
              className="px-5 py-2 rounded-lg text-sm font-medium text-gray-600 bg-gray-100 hover:bg-gray-200 transition-colors"
            >
              Cancelar
            </button>
            <ButtonWithLoader type="submit" isLoading={isLoading}>
              Salvar
            </ButtonWithLoader>
          </div>
        </form>
      </div>
    </div>
  );
};

// ── RepossessionModal ─────────────────────────────────────────────────────────

interface RepossessionModalProps {
  onClose: () => void;
  onSubmit: (observation: string, priority: REPOSSESSION_PRIORITY) => Promise<void>;
  isLoading: boolean;
}

const RepossessionModal: React.FC<RepossessionModalProps> = ({ onClose, onSubmit, isLoading }) => {
  const [observation, setObservation] = useState('');
  const [priority, setPriority] = useState<REPOSSESSION_PRIORITY>(REPOSSESSION_PRIORITY.MEDIUM);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit(observation, priority);
  };

  return (
    <div
      className="fixed inset-0 flex items-center justify-center z-[60]"
      style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}
      onClick={onClose}
    >
      <div
        className="bg-white rounded-xl shadow-2xl w-full max-w-md flex flex-col overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-300">
          <h3 className="font-semibold text-base text-gray-900">Enviar para Recolhimento</h3>
          <button
            onClick={onClose}
            aria-label="Fechar"
            className="w-8 h-8 rounded-full flex items-center justify-center text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
            </svg>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-5 space-y-4">
          <div>
            <label className="block text-xs font-semibold uppercase tracking-wider text-gray-500 mb-2">
              Prioridade
            </label>
            <div className="flex gap-2">
              {([
                { value: REPOSSESSION_PRIORITY.HIGH, label: 'Alta' },
                { value: REPOSSESSION_PRIORITY.MEDIUM, label: 'Média' },
                { value: REPOSSESSION_PRIORITY.LOW, label: 'Baixa' },
              ] as const).map(({ value, label }) => (
                <button
                  key={value}
                  type="button"
                  onClick={() => setPriority(value)}
                  className="flex-1 py-2 px-3 rounded-lg text-sm font-medium border transition-colors"
                  style={priority === value
                    ? { backgroundColor: RED, color: '#fff', borderColor: RED }
                    : { backgroundColor: '#fff', color: '#374151', borderColor: '#d1d5db' }
                  }
                >
                  {label}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-xs font-semibold uppercase tracking-wider text-gray-500 mb-2">
              Observação
            </label>
            <textarea
              value={observation}
              onChange={(e) => setObservation(e.target.value)}
              rows={4}
              placeholder="Adicione uma observação para a equipe de recolhimento..."
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm text-gray-800 placeholder-gray-400 outline-none resize-none"
            />
          </div>

          <div className="flex justify-end gap-3 pt-1">
            <button
              type="button"
              onClick={onClose}
              className="px-5 py-2 rounded-lg text-sm font-medium text-gray-600 bg-gray-100 hover:bg-gray-200 transition-colors"
            >
              Cancelar
            </button>
            <ButtonWithLoader type="submit" isLoading={isLoading}>
              Enviar para recolhimento
            </ButtonWithLoader>
          </div>
        </form>
      </div>
    </div>
  );
};

// ── PaymentArrangementModal ───────────────────────────────────────────────────

const PaymentArrangementModal: React.FC<NegotiationModalProps> = ({
  initialIsOpen = false,
  onClose,
  data,
  onUpdateInteraction,
}) => {
  const [isOpen, setIsOpen] = useState<boolean>(initialIsOpen);
  const [debitmanagmentItem, setDebitmanagmentItem] = useState<IDebitmanagment | null>(null);
  const [callInteractions, setCallInteractions] = useState<ICallInteraction[]>([]);
  const [paymentNegotiations, setPaymentNegotiations] = useState<Array<{
    description: string; dueDate: string; individualAmountPayment: number;
  }>>([]);
  const [loadingPayments, setLoadingPayments] = useState(true);
  const [namePartner, setNamePartner] = useState<string | null>(null);
  const [showRegisterModal, setShowRegisterModal] = useState(false);
  const [savingInteraction, setSavingInteraction] = useState(false);
  const [showRepossessionModal, setShowRepossessionModal] = useState(false);
  const [savingRepossession, setSavingRepossession] = useState(false);
  const [showNegotiationModal, setShowNegotiationModal] = useState(false);

  const { notifySuccess, notifyError } = useToastContext();
  const { auth } = useContext(AuthContext);

  const handleContactRegistration = async (
    notes: string,
    status: CALL_INTERACTION_STATUS,
    scheduledDate?: string,
  ) => {
    setSavingInteraction(true);
    const dateNow = new Date();

    const newInteraction: ICallInteraction = {
      interactionId: auth?.user?.sub?.replace('auth0|', '') || '',
      contactDate: dateNow,
      notes,
      status,
      scheduledDate: scheduledDate ? new Date(scheduledDate) : undefined,
      author: auth?.user.name,
      createdAt: dateNow,
    };

    const insertResult = await createCallInteractions(
      debitmanagmentItem?._id || '',
      debitmanagmentItem?.negotiations?.[0]?._id || '',
      newInteraction,
    );

    if (!insertResult.ok) {
      setSavingInteraction(false);
      return notifyError(insertResult.message || 'Erro ao registrar contato');
    }

    const updatedItem = insertResult.data as IDebitmanagment | undefined;

    if (updatedItem) {
      setDebitmanagmentItem(updatedItem);
      setCallInteractions(
        updatedItem?.negotiations?.flatMap((n) => n.callCenterInteractions || []).filter(Boolean) || [],
      );
      if (onUpdateInteraction) onUpdateInteraction(updatedItem);
    } else {
      setCallInteractions((prev) => [...prev, newInteraction]);

      if (debitmanagmentItem && onUpdateInteraction) {
        const fallback = JSON.parse(JSON.stringify(debitmanagmentItem)) as IDebitmanagment;
        if (!fallback.negotiations?.[0]) {
          fallback.negotiations = [{
            _id: debitmanagmentItem?.negotiations?.[0]?._id || '',
            total: debitmanagmentItem?.amount || 0,
            createdAt: new Date(),
            updatedAt: new Date(),
            paymentNegotiations: [],
            callCenterInteractions: [newInteraction],
          }];
        } else {
          fallback.negotiations[0].callCenterInteractions = [
            ...(fallback.negotiations[0].callCenterInteractions || []),
            newInteraction,
          ];
        }
        onUpdateInteraction(fallback);
      }
    }

    setSavingInteraction(false);
    setShowRegisterModal(false);
    notifySuccess('Registro de contato realizado com sucesso!');
  };

  const handleRepossession = async (observation: string, priority: REPOSSESSION_PRIORITY) => {
    setSavingRepossession(true);
    try {
      const result = await sendRepossessionDebitManagement(
        debitmanagmentItem?.renter?.vehicleId || '',
        debitmanagmentItem?.daysOverdue || 0,
        Number(debitmanagmentItem?.amount) || 0,
        {
          _id: debitmanagmentItem?.renter?.id || '',
          name: debitmanagmentItem?.renter?.name || '',
          whatsapp: debitmanagmentItem?.renter?.whatsapp || '',
        },
        observation,
        priority,
      );

      if (!result.ok) return notifyError(result.message || 'Erro ao enviar para recolhimento');

      setShowRepossessionModal(false);
      handleClose();
      notifySuccess('Recolhimento enviado com sucesso');
    } catch {
      notifyError('Erro ao enviar para recolhimento');
    } finally {
      setSavingRepossession(false);
    }
  };

  const fetchData = async () => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const { _original } = data as any || {};
    const result = _original as IDebitmanagment;
    setIsOpen(initialIsOpen);
    setDebitmanagmentItem(result || null);
    setCallInteractions(result?.negotiations?.flatMap((n) => n.callCenterInteractions || []).filter(Boolean) || []);

    const currentNegotiations = result?.negotiations?.flatMap((n) => n.paymentNegotiations || []).filter(Boolean) || [];

    if (currentNegotiations.length > 0) {
      setPaymentNegotiations(currentNegotiations.map((p) => ({
        description: p.description || '',
        dueDate: String((p as { dueDate?: string }).dueDate || p.paymentDate || ''),
        individualAmountPayment: Number(p.individualAmountPayment) || 0,
      })));
      setLoadingPayments(false);
    } else if (result?.renter?.id) {
      try {
        const paymentsResult = await getRenterPayments(result.renter.id);
        if (!paymentsResult?.ok) throw new Error(paymentsResult?.message);
        setPaymentNegotiations(mapPaymentsToNegotiationRows(paymentsResult.data || []));
      } catch {
        setPaymentNegotiations([]);
        notifyError('Erro ao carregar o detalhamento do débito');
      } finally {
        setLoadingPayments(false);
      }
    } else {
      setPaymentNegotiations([]);
      setLoadingPayments(false);
    }

    if (result?.renter?.partnerId) {
      try {
        const vehicleResult = await getVehicleById(result.renter.vehicleId || '');
        if (!vehicleResult.ok) { setNamePartner(null); return; }
        setNamePartner(vehicleResult.data?.partnerInfo.companyName || null);
      } catch {
        setNamePartner(null);
      }
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialIsOpen, data]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => { if (e.key === 'Escape') handleClose(); };
    if (isOpen) document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const handleClose = () => { setIsOpen(false); onClose(); };

  if (!isOpen) return null;

  const isPartnerVehicle = debitmanagmentItem?.renter?.partnerId && !namePartner?.toUpperCase().includes('LOCAVIBE');
  const formattedAmount = debitmanagmentItem?.amount
    ? formatCurrency(Number(debitmanagmentItem.amount))
    : 'N/A';

  return (
    <>
      <div
        className="fixed inset-0 flex items-center justify-center z-50"
        style={{ backgroundColor: 'rgba(0,0,0,0.4)' }}
        role="dialog"
        aria-modal="true"
        onClick={handleClose}
      >
        <div
          className="bg-white rounded-xl shadow-2xl w-full max-h-[92vh] flex flex-col overflow-hidden"
          style={{ maxWidth: '80%' }}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-center gap-3 px-5 py-4 border-b border-gray-300">
            <div className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: `${RED}22` }}>
              <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5" style={{ color: RED }} viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="flex-1 min-w-0">
              <h2 className="font-semibold text-base leading-tight text-gray-900">Negociação</h2>
              <p className="text-xs mt-0.5 truncate text-gray-500">{debitmanagmentItem?.renter?.name || ''}</p>
            </div>
            <button
              onClick={handleClose}
              aria-label="Fechar"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
              </svg>
            </button>
          </div>

          {/* Content */}
          <div className="overflow-y-auto flex-1 p-5 space-y-5 bg-gray-100">

            {/* Cliente */}
            <div>
              <SectionLabel>Cliente</SectionLabel>
              <div className="rounded-lg overflow-hidden border border-gray-300 bg-white">
                {[
                  { label: 'Nome', value: debitmanagmentItem?.renter?.name || 'N/A' },
                  { label: 'CPF', value: debitmanagmentItem?.renter?.cpf || 'N/A' },
                  {
                    label: 'WhatsApp',
                    value: debitmanagmentItem?.renter?.whatsapp
                      ? <a href={`https://wa.me/55${String(debitmanagmentItem.renter.whatsapp).replace(/\D/g, '')}`} target="_blank" rel="noopener noreferrer" style={{ color: RED }}>{debitmanagmentItem.renter.whatsapp}</a>
                      : 'N/A',
                  },
                ].map((row, idx) => (
                  <div key={idx} className={`flex items-center px-4 py-2.5 text-sm ${idx % 2 !== 0 ? 'bg-gray-50' : ''}`}>
                    <span className="w-1/4 font-medium text-gray-500">{row.label}</span>
                    <span className="text-gray-800">{row.value}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Cards */}
            <div className="grid grid-cols-2 gap-3">
              <InfoCard label="Valor em Aberto">
                <span style={{ color: RED }}>{formattedAmount}</span>
              </InfoCard>
              <InfoCard label="Atraso">
                <span className="text-orange-500">{debitmanagmentItem?.daysOverdue ?? 'N/A'} dias</span>
              </InfoCard>
              <InfoCard label="Veículo de Parceiro">
                {isPartnerVehicle
                  ? <span style={{ color: RED }}>Sim</span>
                  : <span className="text-gray-800">Não</span>
                }
              </InfoCard>
              <InfoCard label="Proprietário">
                <span className="text-gray-800">{namePartner || 'N/A'}</span>
              </InfoCard>
            </div>

            {/* Detalhamento do Débito */}
            <div>
              <SectionLabel>Detalhamento do Débito</SectionLabel>
              <div className="rounded-lg overflow-hidden border border-gray-300">
                {loadingPayments ? (
                  <TableLoadingSkeleton />
                ) : paymentNegotiations.length > 0 ? (
                  <table className="w-full text-sm">
                    <thead>
                      <tr style={{ backgroundColor: RED }}>
                        <th className="text-left font-semibold px-4 py-2.5 text-xs text-white">Descrição</th>
                        <th className="text-center font-semibold px-3 py-2.5 text-xs whitespace-nowrap text-white">Vencimento</th>
                        <th className="text-right font-semibold px-4 py-2.5 text-xs whitespace-nowrap text-white">Valor</th>
                      </tr>
                    </thead>
                    <tbody>
                      {paymentNegotiations.map((row, idx) => (
                        <tr key={idx} className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                          <td className="px-4 py-2.5 text-xs text-gray-700">{row.description}</td>
                          <td className="px-3 py-2.5 text-xs text-center whitespace-nowrap text-gray-700">{formatDate(row.dueDate)}</td>
                          <td className="px-4 py-2.5 text-xs text-right whitespace-nowrap text-gray-700">{formatCurrency(row.individualAmountPayment)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : (
                  <p className="text-sm text-center py-6 text-gray-400 bg-white">Nenhum débito em aberto</p>
                )}
              </div>
            </div>

            {/* Histórico de Interações */}
            <div>
              <SectionLabel>Histórico de Interações</SectionLabel>
              <div className="rounded-lg overflow-hidden border border-gray-300">
                {callInteractions.length > 0 ? (
                  <table className="w-full text-sm">
                    <thead>
                      <tr style={{ backgroundColor: RED }}>
                        <th className="text-left font-semibold px-4 py-2.5 text-xs text-white">Autor</th>
                        <th className="text-center font-semibold px-3 py-2.5 text-xs whitespace-nowrap text-white">Data e Hora</th>
                        <th className="text-left font-semibold px-3 py-2.5 text-xs text-white">Status</th>
                        <th className="text-left font-semibold px-4 py-2.5 text-xs text-white">Observações</th>
                      </tr>
                    </thead>
                    <tbody>
                      {callInteractions.map((interaction, idx) => (
                        <tr key={idx} className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                          <td className="px-4 py-2.5 text-xs text-gray-700">{interaction.author || ''}</td>
                          <td className="px-3 py-2.5 text-xs text-center whitespace-nowrap text-gray-700">{formatDateTime(interaction.contactDate)}</td>
                          <td className="px-3 py-2.5 text-xs text-gray-700 whitespace-nowrap">
                            {interaction.status ? INTERACTION_STATUS_LABELS[interaction.status] : '—'}
                          </td>
                          <td className="px-4 py-2.5 text-xs text-gray-700">{interaction.notes || ''}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : (
                  <p className="text-sm text-center py-6 text-gray-400 bg-white">Nenhuma interação registrada</p>
                )}
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end gap-3 px-5 py-4 border-t border-gray-300 bg-white">
            <button
              onClick={() => setShowNegotiationModal(true)}
              style={{ borderColor: '#f59e0b', color: '#f59e0b' }}
            >
              Negociar débitos
            </button>
            <button
              onClick={() => setShowRepossessionModal(true)}
              className="px-5 py-2 rounded-lg text-sm font-medium border transition-colors"
              style={{ borderColor: RED, color: RED }}
            >
              Enviar para recolhimento
            </button>
            <button
              onClick={() => setShowRegisterModal(true)}
              className="px-5 py-2 rounded-lg text-sm font-medium text-white transition-colors"
              style={{ backgroundColor: RED }}
            >
              Registrar interação
            </button>
          </div>
        </div>
      </div>

      {showRegisterModal && (
        <RegisterInteractionModal
          onClose={() => setShowRegisterModal(false)}
          onSubmit={handleContactRegistration}
          isLoading={savingInteraction}
        />
      )}

      {showRepossessionModal && (
        <RepossessionModal
          onClose={() => setShowRepossessionModal(false)}
          onSubmit={handleRepossession}
          isLoading={savingRepossession}
        />
      )}

      {showNegotiationModal && debitmanagmentItem?.renter?.id && (
        <div
          className="fixed inset-0 flex items-center justify-center z-[60]"
          style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}
          onClick={() => setShowNegotiationModal(false)}
        >
          <div
            className="bg-white rounded-xl shadow-2xl w-full max-w-4xl max-h-[90vh] flex flex-col overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-300">
              <h3 className="font-semibold text-base text-gray-900">Negociar Débitos</h3>
              <button
                onClick={() => setShowNegotiationModal(false)}
                aria-label="Fechar">
                <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
              </button>
            </div>
            <div className="overflow-y-auto flex-1 p-5 bg-gray-50">
              <DebitNegotiationDashboard renterId={debitmanagmentItem.renter.id} />
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default PaymentArrangementModal;
